import { Card, Stack, Title, Text, Badge, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { templates as templatesNs } from '@repletoryreach/sdk-api';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { TemplateStatusBadge } from '../../components/TemplateStatusBadge';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templatesNs.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted)';

type StepState = 'done' | 'pending' | 'failed' | 'warning';

const DOT_COLOUR: Record<StepState, string> = {
  done: 'var(--rr-color-success)',
  failed: 'var(--rr-color-error)',
  warning: 'var(--rr-color-warning)',
  pending: 'var(--rr-color-text-muted)'
};

interface Step {
  label: string;
  at: string | null;
  state: StepState;
  badge?: React.ReactNode;
  detail?: React.ReactNode;
}

export function LifecycleTab({ template }: { template: Template }) {
  const t = useT();

  // A dev-fallback approval writes `approved_at` exactly as a real one does,
  // so the review step used to show a green dot for a template Meta never saw,
  // and the header badge agreed with it. Amber plus a warning is the honest
  // reading of the same two fields.
  const devApproved = template.dev_fallback && Boolean(template.approved_at);

  let reviewState: StepState = 'pending';
  if (devApproved) reviewState = 'warning';
  else if (template.approved_at) reviewState = 'done';
  else if (template.rejection_reason) reviewState = 'failed';

  let reviewDetail: React.ReactNode;
  if (devApproved) {
    reviewDetail = <Alert color="warning" variant="light">{t('templates.view.lifecycle.devFallbackWarning')}</Alert>;
  } else if (template.status === 'rejected' && template.rejection_reason) {
    reviewDetail = <Alert color="red" variant="light">{template.rejection_reason}</Alert>;
  }

  const reviewBadge = devApproved
    ? <Badge size="xs" variant="outline" color="orange">{t('templates.view.lifecycle.devFallbackBadge')}</Badge>
    : undefined;

  const steps: Step[] = [
    { label: t('templates.view.lifecycle.steps.drafted'), at: template.created_at, state: 'done' },
    {
      label: t('templates.view.lifecycle.steps.submitted'),
      at: template.submitted_at,
      state: template.submitted_at ? 'done' : 'pending'
    },
    {
      label: t('templates.view.lifecycle.steps.review'),
      at: template.approved_at,
      state: reviewState,
      badge: reviewBadge,
      detail: reviewDetail
    }
  ];

  // Paused is one of the five lifecycle states and the three fixed steps could
  // not express it, so a paused template read as "review pending" forever.
  if (template.status === 'paused') {
    steps.push({
      label: t('templates.view.lifecycle.steps.paused'),
      at: template.updated_at,
      state: 'warning',
      detail: <Alert color="warning" variant="light">{t('templates.view.lifecycle.pausedDetail')}</Alert>
    });
  }

  steps.push({ label: t('common.updated'), at: template.updated_at, state: 'done' });

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <Title order={4}>{t('templates.view.lifecycle.title')}</Title>
            <TemplateStatusBadge status={template.status} devFallback={template.dev_fallback} metaStatus={template.meta_status} />
          </Group>
          <Stack gap={0}>
            {steps.map((step, i) => (
              <Group
                key={i}
                gap="md"
                align="flex-start"
                wrap="nowrap"
                style={{
                  padding: '12px 0',
                  borderBottom:
                    i < steps.length - 1 ? '1px dashed var(--rr-color-border)' : 'none'
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    marginTop: 6,
                    flexShrink: 0,
                    background: DOT_COLOUR[step.state]
                  }}
                />
                <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="sm" align="baseline" wrap="wrap">
                    <Text role="bodySmall">{step.label}</Text>
                    {step.badge}
                    {step.state === 'pending' && (
                      <Badge size="xs" variant="outline" color="gray">{t('status.pending')}</Badge>
                    )}
                    {step.state === 'failed' && (
                      <Badge size="xs" variant="light" color="red">{t('status.rejected')}</Badge>
                    )}
                  </Group>
                  <Text role="caption" c={MUTED}>
                    {step.at
                      ? `${formatDateTime(step.at)} (${formatRelative(step.at)})`
                      : t('common.notAvailable')}
                  </Text>
                  {step.detail}
                </Stack>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
